import { useQuery } from '@tanstack/react-query';
import { AutocompleteElement, useFormContext } from 'react-hook-form-mui';

import Api from '@barboxx/api';
import { Product } from '@barboxx/models/types';

interface Props {
    disabled?: boolean;
}

interface BaseProductWatchProps {
    product?: Product;
    brandId?: number;
}

export function BaseProductInput({ disabled = false }: Props) {
    const { brandId, product } =
        useFormContext<BaseProductWatchProps>().getValues();

    const productQueryResult = useQuery({
        queryKey: ['products', brandId],
        queryFn: () =>
            Api.Products.Products.getProducts({
                page: 0,
                size: 1000,
                sort: 'name,asc',
                brandId,
                flavor: null,
            }),
        initialData: {
            ok: true,
            problem: null,
            originalError: null,
            data: {
                results: product ? [product] : [],
                total: 1,
                isPaginated: false,
            },
        },
    });

    return (
        <AutocompleteElement
            label="Base Product"
            name="baseProductId"
            loading={productQueryResult.isLoading}
            multiple={false}
            options={productQueryResult.data?.data?.results ?? []}
            transform={{
                output(event, value, reason, details) {
                    if (reason === 'selectOption') {
                        return typeof value === 'string' ? value : value?.id;
                    }
                    if (reason === 'removeOption') {
                        return null;
                    }
                    return value;
                },
            }}
            autocompleteProps={{
                fullWidth: true,
                readOnly: disabled,
                disabled: disabled,
                isOptionEqualToValue(
                    option?: Product,
                    value?: Product | number | null
                ) {
                    const isNumber = typeof value === 'number';

                    if (isNumber) {
                        return option?.id === value;
                    } else {
                        return option?.id === (value?.id ?? value);
                    }
                },
                getOptionLabel(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return `${option?.brand?.name} ${option?.name}`;
                },
                getOptionKey(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return `${option?.id}-${option.productTypeId}`;
                },
            }}
        />
    );
}
